import React from "react";
import { Dialog, Transition } from "@headlessui/react";
import { History, Search, Trash2, Play, Eraser } from "lucide-react";
import { PersistedIntelligenceSearch, formatRelativeTime } from "../Services/IntelligencePersistence";

interface SearchHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  history: PersistedIntelligenceSearch[];
  onApply: (entry: PersistedIntelligenceSearch) => void;
  onDelete: (id: string) => void;
  onClear: () => void;
}

export const SearchHistoryDrawer: React.FC<SearchHistoryDrawerProps> = ({
  open,
  onClose,
  history,
  onApply,
  onDelete,
  onClear,
}) => {
  return (
    <Transition show={open} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={React.Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md h-full bg-white shadow-2xl flex flex-col">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center">
                        <History size={15} />
                      </span>
                      <div>
                        <Dialog.Title className="text-sm font-black text-slate-800">Search History</Dialog.Title>
                        <p className="text-[10px] text-slate-400 font-medium">
                          {history.length} recent search{history.length === 1 ? "" : "es"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {history.length > 0 && (
                        <button
                          type="button"
                          onClick={onClear}
                          className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-1 transition-colors"
                        >
                          <Eraser size={12} /> Clear all
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                        aria-label="Close"
                      >
                        <span className="text-lg leading-none">×</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto px-5 py-4">
                    {history.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center py-10">
                        <History size={26} className="text-slate-200 mb-3" />
                        <p className="text-sm font-bold text-slate-400">No search history yet</p>
                        <p className="text-xs text-slate-300 mt-1 max-w-[240px]">
                          Every search you run in this workspace will appear here so you can quickly re-run it.
                        </p>
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {history.map((entry) => (
                          <li
                            key={entry.id}
                            className="group rounded-xl border border-slate-100 bg-white p-3 hover:border-indigo-200 hover:shadow-sm transition-all"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-xs font-black text-slate-800 truncate">
                                  {entry.term || (
                                    <span className="text-slate-400 font-semibold">Filtered search</span>
                                  )}
                                </p>
                                <p className="text-[10px] text-slate-400 font-medium mt-0.5 flex items-center gap-1 flex-wrap">
                                  {entry.filterCount > 0 && (
                                    <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-bold">
                                      {entry.filterCount} filter{entry.filterCount === 1 ? "" : "s"}
                                    </span>
                                  )}
                                  <span>{formatRelativeTime(entry.createdAt)}</span>
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => onDelete(entry.id)}
                                className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-all"
                                title="Remove from history"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => onApply(entry)}
                              className="mt-2 w-full px-3 py-1.5 rounded-lg bg-slate-800 text-white text-[11px] font-bold hover:bg-slate-700 flex items-center justify-center gap-1.5 transition-colors"
                            >
                              <Play size={12} /> Re-run search
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SearchHistoryDrawer;